import { IAISummaryData, ITranscriptEntry } from './AISummary.types';
import {
  mockGetContainer,
  mockGetSummary,
  mockFetchTranscript,
} from './WebexMockAISummaryService';
import { isAISummaryFeatureEnabled, whenAISummaryReady } from './featureGate';
import { Logger } from '../../../../../logger/src';

// Cached feature gate result to avoid repeated server calls
let featureGateCache: boolean | null = null;
let featureGateCacheTimestamp: number = 0;
const FEATURE_GATE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache

// Low-level service toggle: Controls mock vs real SDK plugin
// Only used if feature gate is enabled
export let USE_MOCK_AI_SUMMARY = false; // Set to true only for mock testing

const MAX_CACHE_ENTRIES = 50;

export class AISummaryService {
  private static instance: AISummaryService;
  private containerInfoCache = new Map<string, any>();

  static getInstance(): AISummaryService {
    if (!AISummaryService.instance) {
      AISummaryService.instance = new AISummaryService();
    }
    return AISummaryService.instance;
  }

  /**
   * Check if AI summary feature is enabled via server-side feature gate.
   * Uses cached result to avoid repeated server calls (5 min TTL).
   * In mock mode, returns true to allow development testing.
   *
   * IMPORTANT: This method now waits for the SDK plugin to be ready before
   * checking the feature gate. This ensures the plugin is initialized before
   * service methods attempt to use it. Follows the same pattern as DSS plugin
   * initialization (see WebexInstanceService.whenDssReady).
   */
  async isFeatureEnabled(): Promise<boolean> {
    // In mock mode (development), skip server check and return true
    if (this.shouldUseMock()) {
      return true;
    }

    const now = Date.now();

    // Return cached result if still valid
    if (featureGateCache !== null && now - featureGateCacheTimestamp < FEATURE_GATE_CACHE_TTL) {
      return featureGateCache;
    }

    try {
      // Wait for AI Summary plugin to be ready before checking feature gate
      // This ensures the plugin is initialized and available for service methods.
      // Timeout: 20 seconds (matches DSS plugin timeout pattern)
      Logger.debug('[AISummary] Waiting for plugin to be ready before checking feature gate...');
      await whenAISummaryReady(20000);
      Logger.debug('[AISummary] Plugin is ready, proceeding with feature gate check');
    } catch (error) {
      Logger.info(`[AISummary] Plugin failed to initialize: ${error}`);
      // If plugin never initializes, disable the feature
      return false;
    }

    // Fetch fresh feature gate result
    const enabled = await isAISummaryFeatureEnabled();
    featureGateCache = enabled;
    featureGateCacheTimestamp = now;
    return enabled;
  }

  private shouldUseMock(): boolean {
    // In tests, if isAISummaryFeatureEnabled is mocked, don't use the service mock
    // This allows tests to properly control the feature gate behavior
    const isFeatureGateMocked = (isAISummaryFeatureEnabled as any).mock !== undefined;
    if (isFeatureGateMocked) {
      return false;
    }

    const useMock =
      USE_MOCK_AI_SUMMARY ||
      (typeof window !== 'undefined' &&
        (window as any).USE_MOCK_AI_SUMMARY === true);

    if (useMock) {
      Logger.debug('[AISummary] Using mock service');
    }
    return useMock;
  }

  private getWebex(): any {
    return (window as any).webex;
  }

  private isPluginAvailable(): boolean {
    try {
      const available = !!(this.getWebex()?.internal?.aisummary);
      if (!available) {
        Logger.info('[AISummary] Plugin not available: window.webex.internal.aisummary is undefined');
      }
      return available;
    } catch (error) {
      Logger.info(`[AISummary] Error checking plugin availability: ${error}`);
      return false;
    }
  }

  async getContainer(containerId: string): Promise<any> {
    // Always check feature gate first (can be mocked in tests)
    if (!(await this.isFeatureEnabled())) {
      Logger.info('[AISummary] Feature gate is disabled, cannot fetch container');
      throw new Error('AI Summary feature is disabled');
    }

    Logger.debug(`[AISummary] Fetching container for: ${containerId}`);

    if (this.shouldUseMock()) {
      Logger.debug(`[AISummary] Using mock container for: ${containerId}`);
      return mockGetContainer(containerId);
    }

    // Check cache
    const cached = this.containerInfoCache.get(containerId);
    if (cached) {
      Logger.debug(`[AISummary] Container cache hit for: ${containerId}`);
      return cached;
    }

    if (!this.isPluginAvailable()) {
      Logger.info(`[AISummary] Cannot fetch container - plugin not available for: ${containerId}`);
      throw new Error('AI Summary plugin not available');
    }

    try {
      const webex = this.getWebex();
      Logger.debug(`[AISummary] Calling webex.internal.aisummary.getContainer for: ${containerId}`);
      const containerInfo = await webex.internal.aisummary.getContainer({
        containerId,
      });

      Logger.debug(`[AISummary] Successfully fetched container for: ${containerId}`);

      // LRU eviction
      if (this.containerInfoCache.size >= MAX_CACHE_ENTRIES) {
        const oldestKey = this.containerInfoCache.keys().next().value;
        if (oldestKey) this.containerInfoCache.delete(oldestKey);
      }
      this.containerInfoCache.set(containerId, containerInfo);
      return containerInfo;
    } catch (error) {
      Logger.info(`[AISummary] Error fetching container for ${containerId}: ${error}`);
      throw error;
    }
  }

  async getSummary(containerInfo: any): Promise<IAISummaryData> {
    // Always check feature gate first (can be mocked in tests)
    if (!(await this.isFeatureEnabled())) {
      Logger.info('[AISummary] Feature gate is disabled, cannot fetch summary');
      throw new Error('AI Summary feature is disabled');
    }

    Logger.debug('[AISummary] Fetching summary data');

    if (this.shouldUseMock()) {
      const containerId = containerInfo.callSessionId || 'default';
      Logger.debug(`[AISummary] Using mock summary for: ${containerId}`);
      return mockGetSummary(containerId);
    }

    if (!this.isPluginAvailable()) {
      Logger.info('[AISummary] Cannot fetch summary - plugin not available');
      throw new Error('AI Summary plugin not available');
    }

    try {
      const webex = this.getWebex();
      Logger.debug('[AISummary] Calling webex.internal.aisummary.getSummary');
      const result = await webex.internal.aisummary.getSummary({ containerInfo });

      Logger.debug('[AISummary] Successfully fetched summary data');

      return {
        shortNote: result.shortNote || '',
        note: result.note || '',
        actionItems: (result.actionItems || []).map((item: any) => ({
          id: item.id,
          content: item.aiGeneratedContent || '',
          editedContent: item.editedContent,
        })),
        transcriptUrl: this.getTranscriptUrl(containerInfo),
        status: 'ready',
      };
    } catch (error) {
      Logger.info(`[AISummary] Error fetching summary: ${error}`);
      throw error;
    }
  }

  getTranscriptUrl(containerInfo: any): string {
    if (this.shouldUseMock()) {
      return containerInfo?.summaryData?.transcriptUrl || '';
    }

    if (!this.isPluginAvailable()) {
      return '';
    }

    const webex = this.getWebex();
    return webex.internal.aisummary.getTranscriptUrl({ containerInfo });
  }

  async fetchTranscript(containerInfo: any): Promise<ITranscriptEntry[]> {
    Logger.debug('[AISummary] Fetching transcript data');

    if (this.shouldUseMock()) {
      const transcriptUrl = containerInfo?.summaryData?.transcriptUrl || '';
      Logger.debug('[AISummary] Using mock transcript');
      return mockFetchTranscript(transcriptUrl);
    }

    if (!this.isPluginAvailable()) {
      Logger.info('[AISummary] Cannot fetch transcript - plugin not available');
      return [];
    }

    try {
      const webex = this.getWebex();
      Logger.debug('[AISummary] Calling webex.internal.aisummary.getTranscript');
      const result = await webex.internal.aisummary.getTranscript({ containerInfo });

      Logger.debug('[AISummary] Successfully fetched transcript data');

      return (result.snippets || []).map((snippet: any) => ({
        speakerName: snippet.speaker?.speakerName || '',
        speakerEmail: snippet.speaker?.speakerEmail || undefined,
        timestamp: snippet.startTime || '',
        content: snippet.content || '',
      }));
    } catch (error) {
      Logger.info(`[AISummary] Error fetching transcript: ${error}`);
      return [];
    }
  }
}
